import { type ClassValue, clsx } from 'clsx';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Prefix Vite `base` for files served from `public/` (e.g. `/integrations/foo.svg`). */
export function publicAssetUrl(path: string): string {
  if (!path.startsWith('/')) return path;
  const base = import.meta.env.BASE_URL;
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${normalizedBase}${path}`;
}

/**
 * Sorts action children based on the following priority:
 * 0: Non-buttons (info, spacers)
 * 1: Tertiary (ghost, outline)
 * 2: Danger / Warning (destructive)
 * 3: Secondary (secondary)
 * 4: Primary / Success (default)
 */
/** Footer buttons whose primary often omits `variant`; they still sort as buttons. */
/** Groups of actions (Cancel · Save) sit right of a lone navigation button (Back). */
const ACTION_GROUP_NAMES = new Set(['FixedFooterActions', 'ModalFixedFooterActions']);

const BUTTON_DISPLAY_NAMES = new Set([
  'Button',
  'FixedFooterButton',
  'ModalFixedFooterButton',
  'DialogFixedFooterButton',
]);

export function sortActionChildren(children: React.ReactNode): React.ReactNode[] {
  return React.Children.toArray(children).sort((a, b) => {
    const getWeight = (child: React.ReactNode): number => {
      if (!React.isValidElement(child)) return 0;
      const props = child.props as { variant?: unknown; className?: unknown };
      const variant = props.variant;
      const className = typeof props.className === 'string' ? props.className : '';

      // Check if it's a button or FixedFooterButton
      const elementType = child.type as unknown;
      const displayName =
        typeof elementType === 'function'
          ? ((elementType as { displayName?: string }).displayName ?? elementType.name)
          : typeof elementType === 'object' && elementType !== null
            ? (elementType as { displayName?: string }).displayName
            : undefined;
      if (displayName !== undefined && ACTION_GROUP_NAMES.has(displayName)) return 5;
      const isButton =
        variant !== undefined ||
        (displayName !== undefined && BUTTON_DISPLAY_NAMES.has(displayName));

      if (!isButton) return 0;

      if (variant === 'ghost' || variant === 'outline') return 1;
      if (variant === 'destructive' || className.includes('destructive')) return 2;
      if (variant === 'secondary') return 3;
      return 4; // Primary (default)
    };

    return getWeight(a) - getWeight(b);
  });
}
